(() => {
  if (window.__anchorScrollInit) return;
  window.__anchorScrollInit = true;

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const readHeaderOffset = () => {
    const candidates = ['--sticky-header-offset', '--header-group-height', '--header-height'];
    for (const el of [document.documentElement, document.body]) {
      if (!el) continue;
      const style = getComputedStyle(el);
      for (const name of candidates) {
        const raw = style.getPropertyValue(name).trim();
        const n = parseFloat(raw);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }
    return 0;
  };

  const getScrollTarget = (element) => {
    if (!element) return null;
    return element.closest('.shopify-section') || element;
  };

  const focusTarget = (target) => {
    if (!target) return;
    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }
    try {
      target.focus({ preventScroll: true });
    } catch (e) {
      target.focus();
    }
  };

  const resolveTarget = (hash) => {
    if (!hash || hash === '#') return null;
    let id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (e) {
      id = hash.slice(1);
    }
    if (!id) return null;
    return document.getElementById(id);
  };

  const computeTop = (hash) => {
    const target = resolveTarget(hash);
    if (!target) return null;
    const scrollTarget = getScrollTarget(target);
    const offset = readHeaderOffset();
    return Math.max(0, scrollTarget.getBoundingClientRect().top + window.pageYOffset - offset);
  };

  const scrollToHash = (hash, behavior = 'smooth') => {
    const target = resolveTarget(hash);
    if (!target) return false;
    const scrollTarget = getScrollTarget(target);
    const offset = readHeaderOffset();
    const top = Math.max(0, scrollTarget.getBoundingClientRect().top + window.pageYOffset - offset);
    window.scrollTo({
      top,
      behavior: prefersReducedMotion() ? 'auto' : behavior
    });
    focusTarget(target);
    return true;
  };

  const instantScrollToTop = (top) => {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, top);
    html.style.scrollBehavior = prev;
  };

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const a = e.target.closest('a[href]');
    if (!a) return;
    if (a.target && a.target !== '_self') return;
    if (a.hasAttribute('download')) return;
    if (a.dataset.noAnchorScroll === 'true') return;

    let url;
    try {
      url = new URL(a.href, window.location.href);
    } catch (err) {
      return;
    }
    if (url.origin !== window.location.origin) return;
    if (url.pathname !== window.location.pathname) return;
    if (!url.hash) return;

    if (scrollToHash(url.hash)) {
      e.preventDefault();
      if (window.location.hash !== url.hash) {
        history.pushState(null, '', url.hash);
      }
    }
  });

  const init = () => {
    const pending = window.__pendingAnchorHash || window.location.hash;
    if (!pending) return;

    let cancelled = false;
    let hashRestored = false;
    let lastSetTop = -1;

    const onUserScroll = () => {
      cancelled = true;
    };

    const correct = () => {
      if (cancelled) return;
      const target = resolveTarget(pending);
      if (!target) return;
      const top = computeTop(pending);
      if (top === null) return;
      if (Math.abs(window.pageYOffset - top) < 2) return;
      instantScrollToTop(top);
      lastSetTop = top;
      if (!hashRestored) {
        focusTarget(target);
        if (window.__pendingAnchorHash) {
          try {
            history.replaceState(null, '', window.location.pathname + window.location.search + pending);
          } catch (e) {}
          window.__pendingAnchorHash = null;
        }
        hashRestored = true;
      }
    };

    window.addEventListener('wheel', onUserScroll, { once: true, passive: true });
    window.addEventListener('touchmove', onUserScroll, { once: true, passive: true });
    window.addEventListener('keydown', (e) => {
      if (['PageDown', 'PageUp', 'ArrowDown', 'ArrowUp', ' ', 'Home', 'End'].includes(e.key)) {
        onUserScroll();
      }
    }, { once: true });

    correct();
    requestAnimationFrame(() => requestAnimationFrame(correct));
    if (document.readyState !== 'complete') {
      window.addEventListener('load', correct, { once: true });
    }
    const delays = [150, 400, 900, 1600];
    delays.forEach((d) => setTimeout(correct, d));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('hashchange', () => {
    if (window.location.hash) scrollToHash(window.location.hash);
  });
})();
